import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Button,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  Alert,
  IconButton,
  Divider,
  Switch,
} from '@mui/material';
import { Close as CloseIcon, Computer as ComputerIcon, Person as PersonIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { adminApiService } from '../services/api';
import { HourSlotsEncoder } from '../utils/hourSlotsEncoder';
import { SchedulingReporter, DayAvailability } from '../types';

type JobType = 'virtual' | 'in-person';

// Hour slots matching the reporter's view (7am-6pm, 12 hours)
const HOUR_SLOTS = Array.from({ length: 12 }, (_, i) => {
  const hour = 7 + i;
  return {
    value: hour,
    label: hour <= 12 ? `${hour}${hour === 12 ? 'pm' : 'am'}` : `${hour - 12}pm`,
  };
});

const getSlotColor = (jobTypes: JobType[] | undefined): string => {
  if (!jobTypes) return '';
  const hasVirtual = jobTypes.includes('virtual');
  const hasInPerson = jobTypes.includes('in-person');
  if (hasVirtual && hasInPerson) return '#7e57c2'; // purple
  if (hasInPerson) return '#e57373'; // red
  return '#42a5f5'; // blue
};

interface AdminAvailabilityEditorProps {
  open: boolean;
  reporter: SchedulingReporter | null;
  date: Date;
  dayAvailability: DayAvailability | undefined;
  onClose: () => void;
  onSave: () => void;
  readOnly?: boolean;
}

export const AdminAvailabilityEditor: React.FC<AdminAvailabilityEditorProps> = ({
  open,
  reporter,
  date,
  dayAvailability,
  onClose,
  onSave,
  readOnly = false,
}) => {
  const [selectedHours, setSelectedHours] = useState<Record<number, JobType[]>>({});
  const [selectedJobTypes, setSelectedJobTypes] = useState<JobType[]>(['virtual']);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMarkedUnavailable, setIsMarkedUnavailable] = useState(false);

  const dateStr = format(date, 'yyyy-MM-dd');
  const displayDate = format(date, 'EEEE, MMMM d, yyyy');

  // Initialize editor state when opened
  useEffect(() => {
    if (!open) return;
    setError(null);

    if (dayAvailability && !dayAvailability.empty) {
      setNotes(dayAvailability.notes || '');
      setIsMarkedUnavailable(dayAvailability.unavailable || false);

      if (dayAvailability.unavailable) {
        setSelectedHours({});
        setSelectedJobTypes(['virtual']);
      } else if (dayAvailability.hourSlots) {
        const hourSlotsData = HourSlotsEncoder.decode(dayAvailability.hourSlots);
        const hasAvail = HourSlotsEncoder.hasAvailability(dayAvailability.hourSlots);

        if (!hasAvail) {
          setSelectedHours({});
          setSelectedJobTypes(['virtual']);
        } else {
          setSelectedHours(hourSlotsData as Record<number, JobType[]>);
          const allJobTypes = HourSlotsEncoder.getJobTypes(dayAvailability.hourSlots) as JobType[];
          setSelectedJobTypes(allJobTypes.length > 0 ? allJobTypes : ['virtual']);
        }
      } else {
        setSelectedHours({});
        setSelectedJobTypes(['virtual']);
      }
    } else {
      setSelectedHours({});
      setSelectedJobTypes(['virtual']);
      setNotes('');
      setIsMarkedUnavailable(false);
    }
  }, [open, dayAvailability, dateStr]);

  const handleHourClick = (hour: number) => {
    if (readOnly) return;
    setSelectedHours(prev => {
      const next = { ...prev };
      if (next[hour]) {
        delete next[hour];
      } else {
        next[hour] = selectedJobTypes;
      }
      return next;
    });
  };

  const handleJobTypesChange = (_: React.MouseEvent<HTMLElement>, newJobTypes: JobType[]) => {
    if (newJobTypes.length > 0) {
      setSelectedJobTypes(newJobTypes);
    }
  };

  const handleQuickFill = (type: 'morning' | 'afternoon' | 'all' | 'clear') => {
    if (readOnly) return;
    const next: Record<number, JobType[]> = {};
    if (type !== 'clear') {
      for (const slot of HOUR_SLOTS) {
        const h = slot.value;
        let shouldSelect = false;
        if (type === 'morning') shouldSelect = h >= 8 && h < 12;
        else if (type === 'afternoon') shouldSelect = h >= 12 && h <= 16;
        else if (type === 'all') shouldSelect = h >= 8 && h <= 16;
        if (shouldSelect) next[h] = selectedJobTypes;
      }
    }
    setSelectedHours(next);
  };

  const handleSave = async () => {
    if (!reporter || readOnly) return;
    try {
      setSaving(true);
      setError(null);

      if (isMarkedUnavailable) {
        await adminApiService.setReporterAvailability({
          reporterId: reporter.reporterId,
          availabilityDate: dateStr,
          hourSlots: 'NNNNNNNNNNNN',
          notes: '',
          unavailable: true,
        });
        onSave();
        return;
      }

      const hasSelected = Object.keys(selectedHours).length > 0;
      if (!hasSelected) {
        // No hours selected — remove availability for the day
        await adminApiService.removeReporterAvailability(reporter.reporterId, dateStr);
        onSave();
        return;
      }

      const hourSlotsString = HourSlotsEncoder.encode(selectedHours);

      await adminApiService.setReporterAvailability({
        reporterId: reporter.reporterId,
        availabilityDate: dateStr,
        hourSlots: hourSlotsString,
        notes: notes || undefined,
        unavailable: false,
      });
      onSave();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save availability');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!reporter || readOnly) return;
    try {
      setSaving(true);
      setError(null);
      await adminApiService.removeReporterAvailability(reporter.reporterId, dateStr);
      onSave();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear availability');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ '& .MuiDrawer-paper': { width: { xs: '100%', sm: 400 }, padding: 2 } }}
    >
      <Box>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h6">
            {readOnly ? 'View' : 'Edit'} Availability
          </Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>

        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
          {reporter?.name || `Reporter ${reporter?.reporterId}`}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {displayDate}
        </Typography>

        {readOnly && (
          <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
            You have read-only access to this reporter's schedule.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 1, mb: 1 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Unavailable Toggle */}
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
          <Typography variant="subtitle2">Mark as Not Available</Typography>
          <Switch
            checked={isMarkedUnavailable}
            onChange={(e) => {
              if (readOnly) return;
              setIsMarkedUnavailable(e.target.checked);
              if (e.target.checked) {
                setSelectedHours({});
                setNotes('');
              }
            }}
            color="error"
            disabled={readOnly}
          />
        </Box>

        {/* Job Type Selector */}
        <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 1, opacity: isMarkedUnavailable ? 0.5 : 1 }}>
          <Typography variant="subtitle2" sx={{ whiteSpace: 'nowrap', flexShrink: 0, lineHeight: 1 }}>Job Types:</Typography>
          <ToggleButtonGroup
            value={selectedJobTypes}
            onChange={handleJobTypesChange}
            size="small"
            disabled={isMarkedUnavailable || readOnly}
          >
            <ToggleButton value="virtual">Virtual</ToggleButton>
            <ToggleButton value="in-person">In-Person</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Quick Fill */}
        {!readOnly && (
          <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 1, opacity: isMarkedUnavailable ? 0.5 : 1 }}>
            <Typography variant="subtitle2" sx={{ whiteSpace: 'nowrap', flexShrink: 0, lineHeight: 1 }}>Quick Fill:</Typography>
            <Stack direction="row" spacing={0.5} sx={{ flex: 1 }}>
              <Button size="small" variant="outlined" onClick={() => handleQuickFill('morning')} disabled={isMarkedUnavailable} sx={{ flex: 1, minWidth: '0 !important', px: 0, py: 0.25, fontSize: '0.75rem' }}>AM</Button>
              <Button size="small" variant="outlined" onClick={() => handleQuickFill('afternoon')} disabled={isMarkedUnavailable} sx={{ flex: 1, minWidth: '0 !important', px: 0, py: 0.25, fontSize: '0.75rem' }}>PM</Button>
              <Button size="small" variant="outlined" onClick={() => handleQuickFill('all')} disabled={isMarkedUnavailable} sx={{ flex: 1, minWidth: '0 !important', px: 0, py: 0.25, fontSize: '0.75rem' }}>All</Button>
            </Stack>
          </Box>
        )}

        {/* Hour Slots */}
        <Typography variant="subtitle2" gutterBottom sx={{ opacity: isMarkedUnavailable ? 0.5 : 1 }}>
          Hours:
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1px',
            mb: 2,
            opacity: isMarkedUnavailable ? 0.5 : 1,
          }}
        >
          {HOUR_SLOTS.map((slot) => {
            const jobTypes = selectedHours[slot.value];
            const color = getSlotColor(jobTypes);
            return (
              <Button
                key={slot.value}
                variant="outlined"
                size="small"
                onClick={() => handleHourClick(slot.value)}
                disabled={isMarkedUnavailable || readOnly}
                sx={{
                  minHeight: 0,
                  height: 28,
                  py: 0,
                  fontSize: '0.8rem',
                  position: 'relative',
                  borderRadius: 0,
                  '&:first-of-type': { borderRadius: '4px 4px 0 0' },
                  '&:last-of-type': { borderRadius: '0 0 4px 4px' },
                  ...(jobTypes ? {
                    backgroundColor: `${color} !important`,
                    borderColor: `${color} !important`,
                    color: 'white !important',
                    '&:hover, &:focus, &:active, &.Mui-focusVisible': {
                      backgroundColor: `${color} !important`,
                      borderColor: `${color} !important`,
                      color: 'white !important',
                      filter: 'brightness(0.85)',
                    },
                  } : {}),
                }}
              >
                {jobTypes?.includes('virtual') && (
                  <ComputerIcon sx={{ position: 'absolute', left: 8, fontSize: '14px', color: 'white' }} />
                )}
                {slot.label}
                {jobTypes?.includes('in-person') && (
                  <PersonIcon sx={{ position: 'absolute', right: 8, fontSize: '14px', color: 'white' }} />
                )}
              </Button>
            );
          })}
        </Box>

        {/* Notes */}
        <TextField
          label="Notes (optional)"
          multiline
          rows={2}
          fullWidth
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={isMarkedUnavailable || readOnly}
          sx={{ mb: 1, opacity: isMarkedUnavailable ? 0.5 : 1 }}
        />

        <Divider sx={{ my: 1 }} />

        {/* Actions */}
        {!readOnly ? (
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ flex: 1, minWidth: 0 }}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
            {dayAvailability && !dayAvailability.empty && (
              <Button variant="outlined" color="error" onClick={handleClear} disabled={saving} sx={{ flex: 1, minWidth: 0 }}>
                Clear Day
              </Button>
            )}
            <Button variant="text" onClick={onClose} disabled={saving} sx={{ flex: 1, minWidth: 0 }}>
              Cancel
            </Button>
          </Stack>
        ) : (
          <Button variant="outlined" onClick={onClose} fullWidth>Close</Button>
        )}
      </Box>
    </Drawer>
  );
};
